"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useEffect, useEffectEvent, useState, useCallback, useRef } from "react";
import posthog from "posthog-js";
import { addAction, recordException } from "@/lib/tracing/otel-browser-logger";
import { maskCapturedNetworkRequest } from "./mask-captured-network-request";
import { AnalyticsContextType, AnalyticsUser, BaseEvent } from "./types";
import { useCookieConsentStore } from "@/entities/cookie-consent/model/use-cookie-consent-store";
import { requiresCookieConsent } from "@/lib/feature-flags";
const AnalyticsContext = createContext<AnalyticsContextType | null>(null);
interface AnalyticsProviderProps {
  children: React.ReactNode;
}
type TrackEvent = {
  event: BaseEvent;
  properties: Record<string, any>;
};
type IdentifyEvent = {
  user: AnalyticsUser;
};

// Sends a tracked event straight to PostHog (and SigNoz with performance consent).
const sendTrack = (event: BaseEvent, properties: Record<string, any> = {}, hasPerformanceConsent: boolean) => {
  const eventName = `${event.category}:${event.action}`;
  const eventProperties = {
    ...properties,
    severity: event.severity,
    description: event.description,
    timestamp: new Date().toISOString()
  };
  try {
    // Track in PostHog (requires analytics consent)
    // PostHog respects opt-out state internally
    posthog.capture(eventName, eventProperties);

    // Also track in SigNoz for dual observability
    // This helps correlate product events with distributed traces
    if (hasPerformanceConsent && typeof window !== "undefined" && process.env.NEXT_PUBLIC_TRACING_ENABLED === "true") {
      addAction(eventName, eventProperties);
    }
  } catch (error) {
    posthog.captureException(error, {
      event,
      properties
    });
  }
};

// Identifies the user straight to PostHog (and SigNoz with performance consent).
const sendIdentify = (user: AnalyticsUser, hasPerformanceConsent: boolean) => {
  try {
    // Identify the user in PostHog (requires analytics consent)
    posthog.identify(user.userId, {
      role: user.userRole,
      ...(user.organizationId && {
        organizationId: user.organizationId
      })
    }, {
      userId: user.userId
    });

    // Log user identification for correlation in SigNoz
    // Note: SigNoz doesn't have setGlobalAttributes, so we log identification as an action
    // Privacy: Only non-PII fields logged (COV-75) - use userId to look up email/name if needed
    if (hasPerformanceConsent && typeof window !== "undefined" && process.env.NEXT_PUBLIC_TRACING_ENABLED === "true") {
      const identifyAttributes: Record<string, string> = {
        userId: user.userId
      };
      if (user.userRole) identifyAttributes.userRole = user.userRole;
      if (user.organizationId) identifyAttributes.orgId = user.organizationId;
      if (user.region) identifyAttributes.region = user.region;
      addAction("user.identified", identifyAttributes);
    }
  } catch (error) {
    posthog.captureException(error, {
      userId: user.userId,
      organizationId: user.organizationId
    });
  }

  // Organization tracking - group by ID only, name omitted for privacy (COV-75)
  if (!user.organizationId) return;
  try {
    // Group by organization for multi-tenant analytics
    // Note: organizationName omitted - can be correlated to actual companies
    posthog.group("organization", user.organizationId);
  } catch (error) {
    posthog.captureException(error, {
      userId: user.userId,
      organizationId: user.organizationId
    });
  }
};
export function AnalyticsProvider({
  children
}: AnalyticsProviderProps) {
  const [isReady, setIsReady] = useState(false);
  // Events held until PostHog is ready and, where required, consented. Never
  // rendered, so a ref: the flush below runs on the readiness and consent edge.
  const queueRef = useRef<(IdentifyEvent | TrackEvent)[]>([]);
  const hasTrackedConsentRef = useRef(false);

  // Get consent state - only use if consent is required
  // Use store directly instead of useCookieConsent hook to avoid requiring QueryClient
  // AnalyticsProvider renders outside QueryProvider, so we can't use RPC-dependent hooks
  const needsConsent = requiresCookieConsent();
  const {
    hasConsented,
    status: consentStatus,
    categories
  } = useCookieConsentStore();

  // Determine if we have analytics consent
  const hasAnalyticsConsent = !needsConsent || hasConsented && categories.analytics;

  // Determine if we have performance consent (for HyperDX)
  const hasPerformanceConsent = !needsConsent || hasConsented && categories.performance;

  // Process queued events when ready and we have consent
  useEffect(() => {
    if (!isReady || !hasAnalyticsConsent || queueRef.current.length === 0) return;
    const queued = queueRef.current;
    queueRef.current = [];
    queued.forEach(event => {
      if ("user" in event) {
        sendIdentify(event.user, hasPerformanceConsent);
      } else {
        sendTrack(event.event, event.properties, hasPerformanceConsent);
      }
    });
  }, [isReady, hasAnalyticsConsent, hasPerformanceConsent]);

  // Initialize PostHog with consent-aware configuration, once on mount; the
  // consent state is read then, and later changes are handled by the effect
  // below.
  const readInitialConsent = useEffectEvent(() => ({
    needsConsent,
    hasAnalyticsConsent
  }));
  useEffect(() => {
    if (typeof window === "undefined" || !process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      return;
    }
    const {
      needsConsent: needsConsent_0,
      hasAnalyticsConsent: hasAnalyticsConsent_0
    } = readInitialConsent();

    // Determine PostHog configuration based on region and consent
    const config: Parameters<typeof posthog.init>[1] = {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      capture_pageview: false,
      // We'll handle pageviews manually
      capture_performance: true,
      error_tracking: {
        captureExtensionExceptions: true
      },
      capture_exceptions: true,
      session_recording: {
        maskInputOptions: {
          password: true,
          email: true,
          tel: true
        },
        // Presigned AWS URLs (the live viewer, replay screenshots) reach the
        // recording's network timing; they are cut to their host.
        maskCapturedNetworkRequestFn: maskCapturedNetworkRequest
      },
      loaded: () => setIsReady(true)
    };

    // If consent is required (UK/EU), configure for GDPR compliance
    if (needsConsent_0) {
      // Use memory persistence until consent - no cookies/localStorage
      config.persistence = hasAnalyticsConsent_0 ? "localStorage+cookie" : "memory";

      // Disable session recording until consent is given
      if (!hasAnalyticsConsent_0) {
        config.disable_session_recording = true;
      }
    }
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, config);
  }, []);

  // Handle consent changes - reconfigure PostHog when user accepts/rejects
  useEffect(() => {
    if (typeof window === "undefined" || !isReady) return;

    // Only handle consent changes for regions that require consent
    if (!needsConsent) return;
    if (hasAnalyticsConsent) {
      // User gave consent - enable full tracking
      posthog.opt_in_capturing();

      // Re-enable session recording with updated config
      posthog.set_config({
        persistence: "localStorage+cookie",
        disable_session_recording: false
      });

      // Start session recording if enabled in project settings
      posthog.startSessionRecording();
    } else if (hasConsented && !categories.analytics) {
      // User explicitly rejected analytics - opt out
      posthog.opt_out_capturing();
    }
  }, [hasAnalyticsConsent, hasConsented, needsConsent, isReady, categories.analytics]);

  // Track consent decision when it changes (GDPR compliant with cookieless mode)
  useEffect(() => {
    // Only track once when consent decision is made
    if (!hasConsented || !isReady || hasTrackedConsentRef.current) return;

    // Track consent event - allowed pre-consent with memory persistence
    // This uses a privacy-preserving hash (GDPR compliant)
    posthog.capture("cookie_consent_decision", {
      accepted: categories.analytics,
      region: needsConsent ? "uk_eu" : "australia",
      timestamp: new Date().toISOString()
    });
    hasTrackedConsentRef.current = true;
  }, [hasConsented, categories.analytics, isReady, needsConsent]);
  const track = useCallback((event_0: BaseEvent, properties: Record<string, any> = {}) => {
    if (!isReady) {
      queueRef.current.push({
        event: event_0,
        properties
      });
      return;
    }

    // For UK/EU without consent, queue events for when/if consent is given
    if (needsConsent && !hasAnalyticsConsent) {
      queueRef.current.push({
        event: event_0,
        properties
      });
      return;
    }
    sendTrack(event_0, properties, hasPerformanceConsent);
  }, [isReady, needsConsent, hasAnalyticsConsent, hasPerformanceConsent]);
  const identify = useCallback((user: AnalyticsUser) => {
    if (!isReady) {
      queueRef.current.push({
        user
      });
      return;
    }

    // Identify calls associate data with a specific user, which requires consent
    // For UK/EU without consent, queue for when/if consent is given
    if (needsConsent && !hasAnalyticsConsent) {
      queueRef.current.push({
        user
      });
      return;
    }
    sendIdentify(user, hasPerformanceConsent);
  }, [isReady, needsConsent, hasAnalyticsConsent, hasPerformanceConsent]);
  const captureException = useCallback((error: any, context?: Record<string, any>) => {
    try {
      // Capture in PostHog (requires analytics consent)
      posthog.captureException(error, context);

      // Also capture in SigNoz
      if (hasPerformanceConsent && typeof window !== "undefined" && process.env.NEXT_PUBLIC_TRACING_ENABLED === "true") {
        recordException(error instanceof Error ? error : new Error(String(error)), context);
      }
    } catch (e) {
      console.error("Failed to capture exception:", e);
    }
  }, [hasPerformanceConsent]);
  const value: AnalyticsContextType = {
    track,
    identify,
    isReady,
    captureException
  };
  return <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>;
}
export const useAnalytics = () => {
  const $ = _c(1);
  const context = useContext(AnalyticsContext);
  if (!context) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        track: _temp,
        identify: _temp2,
        isReady: false,
        captureException: _temp3
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  return context;
};
function _temp() {}
function _temp2() {}
function _temp3() {}